import { Dayjs } from 'dayjs';
import { AccessToken } from '../../user/interfaces';
import { ReportFileType } from '../../../constants';

export interface ReportFilters {
  startDate?: string | Date | Dayjs;
  endDate?: string | Date | Dayjs;
  operatory?: string[];
  officeId?: string[];
  createdBy?: string[];
  updatedBy?: string[];
  patientName?: string;
  status?: string[];
  createdAt?: boolean;
  providerId?: string;
  search?: string;
  includePeriodChartRowsOnly?: boolean;
}

export interface GetReportsByFilterPayloadType extends AccessToken {
  filters: ReportFilters;
}

export interface GetActiveUsersSummaryPayloadType extends AccessToken {
  data: { startDate: string; endDate: string };
}

export interface writebackAnalysisType {
  providerId?: string;
  procedureCodes?: string[];
  hasMatchingTransactions?: boolean;
  matchingProviderIdCodes?: string[];
  sikkaAppointmentStatus?: string;
  reason?: string;
}

export enum PerioChartStatus {
  COMPLETE = 'complete',
  PAUSED = 'paused',
  SUBMITTED = 'submitted'
}

export enum TcNotesStatus {
  GENERATED = 'Generated',
  NOT_GENERATED = 'Not Generated'
}

export interface TcNotesReportRef {
  tcNotes?: {
    url?: string | null;
  };
}

export interface PerioChartDataType {
  status: PerioChartStatus | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  pausedAt: string | null;
  pausedCount: number;
  completedAt: string | null;
  submissionHistory: {
    submittedBy: string | null;
    writebackId: string | null;
    msg: string | null;
    submittedAt: string | null;
    posted: boolean | null;
  }[];
  fileKey: string | null;
}

export interface ReportDataType {
  id: string;
  appointmentId?: string;
  patientId?: string;
  patientEmail?: string;
  reportUrl?: string;
  description?: string;
  procedureCodes?: string[];
  procedureKeys?: string[];
  patientName?: string;
  appointmentTime?: string;
  appointmentDate?: string;
  appointmentLength?: string;
  operatory?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  perioCreatedAt?: string;
  perioUpdatedAt?: string;
  userName?: string;
  providerId?: string;
  totalCost?: string;
  writebackStatus?: string;
  writebackAnalysis?: writebackAnalysisType;
  writebackIds: WritebackIdsType;
  retryWriteback?: boolean;
  writebackStatusRequested?: boolean;
  perioChart?: PerioChartDataType | null;
  pausedAt?: string;
  pausedTime?: string[];
  stoppedAt?: string;
  reportProcessingAt?: string;
  initialNotificationSentAt?: string;
  reportCompletedAt?: string;
  finalNotificationSentAt?: string;
  submittedAt?: string;
  submittedForWritebackAt?: string;
  writebackSuccessAt?: string;
  tokens?: {
    totalReportCost?: string | number;
    totalAnalysisCost?: string | number;
    totalReportSumaryCost?: string | number;
    totalSpecialistReportCost?: string | number;
    totalAfterCareSummaryReportCost?: string | number;
    totalFinancialNotesCost?: string | number;
  };
  tmfReport?: boolean;
  hasTcNotes?: TcNotesStatus;
}

export interface GetReportsDataType {
  data: ReportDataType[];
  totalCount: number;
  stats?: {
    total: number;
    submitted: number;
    complete: number;
    paused: number;
    start: number;
    totalReportCost?: number;
  };
  writebackStats?: {
    total: number;
    success: number;
    pending: number;
    failed: number;
  };
}

export interface AppointmentIds {
  appointmentIds: string[];
}
export interface DeleteTrialUsersReportsPayloadType extends AccessToken {
  data: AppointmentIds;
}

export interface GetReportWritebackFailureAnalysisPayloadType
  extends AccessToken {
  data: {
    appointmentId: string;
    officeId: string;
  };
}

export interface GetAdminReportFilesPayloadType extends AccessToken {
  data: {
    officeId: string;
    appointmentId: string;
    type?: ReportFileType[];
  };
}

export interface AdminWritebackRecoveryPayloadType extends AccessToken {
  data: {
    appointmentId: string;
    officeId: string;
  };
}

export type AdminWritebackRecoveryActionTaken = 'queued_retry';

export interface AdminWritebackRecoveryResponseType {
  statusCode: number;
  msg: string;
  data: {
    actionTaken: AdminWritebackRecoveryActionTaken;
    retryWriteback: boolean;
    writebackIds: WritebackIdsType;
    writebackStatus: string;
    writebackStatusRequested: boolean;
  };
}

export type ReportFilesFinalReportContent =
  | Record<string, unknown>
  | string
  | null;

export interface ReportFilesDataType {
  officeId: string;
  appointmentId: string;
  patientId?: string;
  files?: {
    finalReport?: ReportFilesFinalReportContent;
    analysis?: string | null;
  };
}

export type WritebackStatusEnum = 'Success' | 'Pending' | 'Failed' | 'Deleted';

export type WritebackEntryType =
  | {
      status: WritebackStatusEnum;
      message: string;
      pmsPostTime: string;
    }
  | WritebackStatusEnum
  | {};

export type WritebackIdsType = {
  [writebackId: string]: WritebackEntryType;
};

export interface AdminAppointmentType {
  appointmentId: string;
  patientName: string;
  appointmentTime: string;
  appointmentLength: string;
  status: string;
}

export interface AdminOperatoryAppointmentsType {
  operatory: string;
  appointments: AdminAppointmentType[];
}

export interface AdminAppointmentsDataType {
  operatories: string[];
  appointments: AdminOperatoryAppointmentsType[];
}

export interface GetAdminAppointmentsResponseType {
  statusCode: number;
  msg: string;
  data: AdminAppointmentsDataType;
}

export interface GetAdminAppointmentsPayloadType extends AccessToken {
  data: {
    officeId: string;
    date: string;
  };
}
